import * as React from "react";
import * as _ from "lodash";
import { IContractsSummaryResponse } from "@nkr/store/index";
import { ICenterContractSummary } from "@nkr/models/index";
import { VersionedFileEntityType } from "@nkr/enums/index";
import { Table, FormControl, Glyphicon, Button, ButtonGroup } from "react-bootstrap";
import styled from 'styled-components';
import { downloadFile } from '@nkr/services/download-file';


const TableWrapper = styled(Table)`
    & > thead > tr > th,
    & > tbody > tr > td {
        text-align: center;
    }

    & > tbody > tr > td {
        vertical-align: middle;
    }
`;

interface IProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterContracts: (centerId: number) => Promise<IContractsSummaryResponse>,
    getCenterContractsExport: (searchValue: string, centerType: number, centerId: number) => Promise<any>
    getVersionedFile: (centerId: number, id: number) => void
}

interface IState {
    sortType: string,
    sortDirection: string,
    searchValue: string,
    centerType: number,
    centerContracts: ICenterContractSummary[]
}

class CenterContractGrid extends React.Component<IProps, {}> {
    readonly state: IState = {
        centerContracts: [],
        sortType: 'alias',
        sortDirection: 'asc',
        searchValue: '',
        centerType: 1
    }

    componentDidMount = () => {
        this.getData();
    }

    sortList = (sortType: string) => {
        let sortDirection = this.state.sortDirection;

        if (this.state.sortType == sortType) {
            if (this.state.sortDirection == 'asc') {
                sortDirection = 'desc';
            } else {
                sortDirection = 'asc';
            }
        } else {
            sortDirection = 'asc';
        }

        this.setState({
            sortType,
            sortDirection
        });
    }

    getData = () => {
        this.props.showLoading("Getting report data, one moment please...").then(() => {
            this.props.getCenterContracts(0)
                .then((response: IContractsSummaryResponse) => {
                    this.setState({
                        centerContracts: response.data.results,
                        filteredContracts: response.data.results
                    });

                    this.props.hideLoading();
                }).catch((ex) => {
                    alert("An error occured while loading report data");
                    this.props.hideLoading();
                });
        });
    }

    handleFilterChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let newValue = e.currentTarget.value.toLowerCase();

        this.setState({
            searchValue: newValue
        });
    }

    handleCenterTypeChange = (centerType: number) => {
        this.setState({
            centerType
        });
    }

    getFilteredAndSortedContracts = () => {
        let list = this.state.centerContracts.filter(contract => {
            let aliasSearch = this.state.searchValue != '' ? contract.alias.toLowerCase().includes(this.state.searchValue) : true;

            let centerTypeSearch = true;

            if (this.state.centerType == 1) {
                centerTypeSearch = contract.isMemberCenter;
            }

            if (this.state.centerType == 2) {
                centerTypeSearch = !contract.isMemberCenter;
            }

            return aliasSearch && centerTypeSearch;
        });

        if (this.state.sortType == 'alias') {
            return _.orderBy(list, [item => item.alias.toLowerCase()], [this.state.sortDirection]);
        } else {
            return _.orderBy(list, [item => item.name.toLowerCase()], [this.state.sortDirection]);
        }
    }


    handleExport = (centerId: number) => {
        let searchValue = this.state.searchValue;
        let centerType = this.state.centerType;

        this.props.showLoading("Exporting CSV file, one moment please...").then(() => {
            this.props.getCenterContractsExport(searchValue, centerType, centerId).then((response: any) => {

                downloadFile(response.data, `center-contacts.csv`, 'text/csv');

                this.props.hideLoading();
            });
        });
    }


    render = () => {
        const contractTypes = VersionedFileEntityType;

        const filteredContracts = this.getFilteredAndSortedContracts();

        return (
            <>
                <h3 style={{ marginBottom: 20 }}>Center Contracts Summary</h3>

                <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', width: '75%' }}>
                        <div style={{ width: '33%', marginRight: 20 }}>
                            <FormControl
                                type="text"
                                value={this.state.searchValue}
                                placeholder="Type to Search by Center Alias"
                                onChange={this.handleFilterChange}
                            />
                        </div>

                        <div>
                            <ButtonGroup>
                                <Button bsStyle={this.state.centerType == 0 ? 'primary' : 'default'} onClick={() => this.handleCenterTypeChange(0)}>All Centers</Button>
                                <Button bsStyle={this.state.centerType == 1 ? 'primary' : 'default'} onClick={() => this.handleCenterTypeChange(1)}>Member Centers</Button>
                                <Button bsStyle={this.state.centerType == 2 ? 'primary' : 'default'} onClick={() => this.handleCenterTypeChange(2)}>Participant Centers</Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    <div>
                        <Button onClick={() => this.handleExport(0)}>Export CSV</Button>
                    </div>
                </div>

                <TableWrapper striped bordered condensed hover>
                    <thead>
                        <tr>
                            <th>Rank</th>
                            <th style={{ width: '9%' }}><a onClick={() => this.sortList('alias')} style={{ cursor: "pointer" }}>Alias {this.state.sortType == 'alias' ? <Glyphicon glyph={this.state.sortDirection == 'asc' ? 'chevron-up' : 'chevron-down'} /> : null}</a></th>
                            <th style={{ width: '25%' }}><a onClick={() => this.sortList('name')} style={{ cursor: "pointer" }}>Name {this.state.sortType == 'name' ? <Glyphicon glyph={this.state.sortDirection == 'asc' ? 'chevron-up' : 'chevron-down'} /> : null}</a></th>
                            <th>Donor Agreement</th>
                            <th>Recipient Agreement</th>
                            <th>Center Contract</th>
                            <th>SRTR Graft Survival</th>
                            <th>SRTR Patient Survival</th>
                            <th>All In Agreement</th>
                            <th>Business Associate Agreement</th>
                            <th>Donor Protection Addendum</th>
                            <th>Partner Center Agreement</th>
                            <th>DCN Agreement</th>
                            <th>Donor Shield Agreement</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredContracts.map((center, rowCnt) => {
                            let donorAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterDonorAgreement);
                            let recipientAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterRecipientAgreement);
                            let centerContract = center.contracts.find(x => x.contractType === contractTypes.CenterContract);
                            let SrtrGraftSurvival = center.contracts.find(x => x.contractType === contractTypes.CenterGraftSurvival);
                            let SrtrPatientSurvival = center.contracts.find(x => x.contractType === contractTypes.CenterPatientSurvival);
                            let allInAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterAllInAgreement);
                            let businessAssociateAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterBaaFile);
                            let donorProtectionAddendum = center.contracts.find(x => x.contractType === contractTypes.CenterDonorProtectionAddendum);
                            let partnerCenterAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterPartnerCenterAgreement);
                            let dcnAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterDCNAgreement);
                            let donorShieldAgreement = center.contracts.find(x => x.contractType === contractTypes.CenterDonorShieldAgreement);

                            return (
                                <tr key={'center-' + rowCnt}>
                                    <td>{rowCnt + 1}</td>
                                    <td>{center.alias}</td>
                                    <td>{center.name}</td>
                                    <td>{donorAgreement != null ? <a id={donorAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, donorAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{recipientAgreement != null ? <a id={recipientAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, recipientAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{centerContract != null ? <a id={centerContract.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, centerContract.id)}>View </a> : 'N/A'}</td>
                                    <td>{SrtrGraftSurvival != null ? <a id={SrtrGraftSurvival.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, SrtrGraftSurvival.id)}>View </a> : 'N/A'}</td>
                                    <td>{SrtrPatientSurvival != null ? <a id={SrtrPatientSurvival.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, SrtrPatientSurvival.id)}>View </a> : 'N/A'}</td>
                                    <td>{allInAgreement != null ? <a id={allInAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, allInAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{businessAssociateAgreement != null ? <a id={businessAssociateAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, businessAssociateAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{donorProtectionAddendum != null ? <a id={donorProtectionAddendum.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, donorProtectionAddendum.id)}>View </a> : 'N/A'}</td>
                                    <td>{partnerCenterAgreement != null ? <a id={partnerCenterAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, partnerCenterAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{dcnAgreement != null ? <a id={dcnAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, dcnAgreement.id)}>View </a> : 'N/A'}</td>
                                    <td>{donorShieldAgreement != null ? <a id={donorShieldAgreement.id.toString()} style={{ cursor: 'default' }} target='_blank' onClick={() => this.props.getVersionedFile(center.id, donorShieldAgreement.id)}>View </a> : 'N/A'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </TableWrapper>
            </>
        );
    }
}
export default CenterContractGrid;